import { Box, Button, Typography } from '@mui/material'
import React, { useContext } from 'react'
import { WidgetContext } from 'src/contexts/WidgetContext';
import { PureLightThemeCaaS } from 'src/theme/schemes/PureLightTheme';
import { redirectToStore } from 'src/utils/getAppVersion';
import { useAuth0 } from "@auth0/auth0-react";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { isPlatform } from "@ionic/react";
import { trackSignupScreenSeenAt } from "src/utils/analyticsCapFirebase";

interface BenchButtonsToShowProps {
    handleSignUpForFree: (event: React.MouseEvent) => void;
}

const BenchButtonsToShow = ({ handleSignUpForFree }: BenchButtonsToShowProps) => {

    const { isLoggedInAuth0ExperienceGlobal } = useContext(WidgetContext);
    const { loginWithRedirect } = useAuth0();
    // Check if running on native platform (iOS/Android)
    const isNativePlatform = Capacitor.isNativePlatform();
    const isHybridPlatform = isPlatform("hybrid");
    const isRunningNatively = isNativePlatform || isHybridPlatform;

    // Handler for Download App button - redirects to App Store or Google Play
    const handleDownloadApp = async (event: React.MouseEvent) => {
        event.stopPropagation();
        await redirectToStore();
    };

    // Handler for "Go Premium" button - saves intent to open payment drawer after login
    const handleGoPremium = async (event: React.MouseEvent) => {
        event.stopPropagation();



        // Save intent to open payment drawer after login in localStorage
        // Works for both Web and Capacitor (localStorage persists in native app context)
        localStorage.setItem("openPaymentAfterLogin", "manageMembership");
        // console.log("💾 [Go Premium] Saved openPaymentAfterLogin=manageMembership to localStorage");
        // console.log("💾 [Go Premium] Platform:", isRunningNatively ? "Capacitor" : "Web");

        // Execute login with redirect - same logic as handleSignUpForFree
        await trackSignupScreenSeenAt("locked_feature", "bench_premium");
        await loginWithRedirect({
            async openUrl(url) {
                // Redirect using Capacitor's Browser plugin
                await Browser.open({
                    url,
                    windowName: "_self",
                });
            },
        });
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1, width: "100%", px: 2 }}>
            {/* First Button - Sign In / Create Account For Free (Blue) */}
            <Button
                variant="contained"
                onClick={handleSignUpForFree}
                sx={{
                    width: "100%",
                    maxWidth: "400px",
                    bgcolor: "#5569ff", // Primary blue color from theme
                    color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                    borderRadius: 2,
                    p: 0.5,
                    textTransform: "none",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 0,
                    "&:hover": {
                        bgcolor: "#5569ff",
                    },
                }}
            >
                <Typography
                    sx={{
                        fontSize: "14px",
                        fontWeight: "bold",
                        color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                    }}
                >
                    Sign In / Create Account For Free
                </Typography>
                <Typography
                    sx={{
                        fontSize: "13px",
                        fontWeight: "normal",
                        color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                        textAlign: "center",
                    }}
                >
                    Get even more credits
                </Typography>
                <Typography
                    sx={{
                        fontSize: "13px",
                        fontWeight: "normal",
                        color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                        textAlign: "center",
                    }}
                >
                    Save your leagues
                </Typography>
            </Button>

            {/* Second Button - Download App and Sign In Free (Light Gray)  (only show it if we are not running natively)*/}
            {!isRunningNatively && (
                <Button
                    variant="contained"
                    onClick={handleDownloadApp}
                    sx={{
                        width: "100%",
                        maxWidth: "400px",
                        bgcolor: `${PureLightThemeCaaS.colors.alpha.gray[30]}`,
                        color: `${PureLightThemeCaaS.colors.alpha.black[100]}`,
                        borderRadius: 2,
                        p: 0.5,
                        textTransform: "none",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 0,
                        "&:hover": {
                            bgcolor: `${PureLightThemeCaaS.colors.alpha.gray[30]}`,
                        },
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: "14px",
                            fontWeight: "bold",
                            color: `${PureLightThemeCaaS.colors.alpha.black[100]}`,
                        }}
                    >
                        Download App and Sign In Free
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: "13px",
                            fontWeight: "normal",
                            color: `${PureLightThemeCaaS.colors.alpha.black[100]}`,
                            textAlign: "center",
                        }}
                    >
                        Get even more credits
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: "13px",
                            fontWeight: "normal",
                            color: `${PureLightThemeCaaS.colors.alpha.black[100]}`,
                            textAlign: "center",
                        }}
                    >
                        Save your leagues
                    </Typography>
                </Button>
            )}

            {/* Third Button - Go Premium (Orange) */}
            <Button
                variant="contained"
                onClick={handleGoPremium}
                sx={{
                    width: "100%",
                    maxWidth: "400px",
                    bgcolor: `${PureLightThemeCaaS.colors.alpha.orange[100]}`,
                    color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                    borderRadius: 2,
                    p: 0.5,
                    textTransform: "none",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 0,
                    "&:hover": {
                        bgcolor: `${PureLightThemeCaaS.colors.alpha.orange[100]}`,
                    },
                }}
            >
                <Typography
                    sx={{
                        fontSize: "14px",
                        fontWeight: "bold",
                        color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                    }}
                >
                    Go Premium
                </Typography>
                <Typography
                    sx={{
                        fontSize: "13px",
                        fontWeight: "normal",
                        color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                        textAlign: "center",
                    }}
                >
                    24/7 priority access.
                </Typography>
                <Typography
                    sx={{
                        fontSize: "13px",
                        fontWeight: "normal",
                        color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                        textAlign: "center",
                    }}
                >
                    No more benching.
                </Typography>
            </Button>
        </Box>
    )
}

export default BenchButtonsToShow